import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { BarChart3, FileText, LogIn, Users, Euro, Calendar, ArrowUpRight } from "lucide-react";
import ExportExcelButton from "../components/ExportExcelButton";

function eur(v) {
  const n = Number(v);
  if (!isFinite(n)) return "€0,00";
  return n.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Relatorios() {
  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = new Date(Date.now() - 365 * 86400 * 1000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(yearAgo);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/relatorios", { params: { from, to } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [from, to]);

  const exportParams = useMemo(() => ({ from, to }), [from, to]);

  if (loading && !data) {
    return <div className="py-14 text-center text-sm font-manrope text-neutral-500">A carregar relatórios…</div>;
  }
  if (!data) return null;

  const { totais, orcamentos, servicos_por_utilizador, acessos_por_utilizador } = data;

  return (
    <div className="space-y-8" data-testid="relatorios-page">
      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
              De
            </label>
            <input
              data-testid="rel-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="bg-white border border-zinc-200 rounded-sm px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
              Até
            </label>
            <input
              data-testid="rel-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="bg-white border border-zinc-200 rounded-sm px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
            />
          </div>
          <div className="text-xs font-manrope text-neutral-500">
            <Calendar className="w-3.5 h-3.5 inline mr-1" strokeWidth={2} />
            Intervalo aplicado a registos e acessos
          </div>
        </div>
        <ExportExcelButton
          endpoint="/admin/relatorios/export/xlsx"
          filename={`relatorios_${from}_a_${to}.xlsx`}
          params={exportParams}
          testid="export-relatorios-btn"
        />
      </div>

      {/* Orçamentos */}
      <section data-testid="section-orcamentos">
        <SectionHeader
          eyebrow="Faturação"
          title="Orçamentos emitidos"
          icon={FileText}
          hint="Contagem e valor de todos os serviços do tipo Orçamento no intervalo escolhido."
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <BigStat
            label="Nº total de orçamentos"
            value={orcamentos.count}
            testid="orcamentos-count"
            accent="#0047FF"
          />
          <BigStat
            label="Valor total"
            value={eur(orcamentos.valor_total)}
            testid="orcamentos-valor"
            accent="#15803D"
          />
          <BigStat
            label="Valor médio"
            value={eur(orcamentos.count ? orcamentos.valor_total / orcamentos.count : 0)}
            testid="orcamentos-media"
            accent="#B45309"
          />
        </div>
        {orcamentos.por_estado?.length > 0 && (
          <div className="mt-4 bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm font-manrope">
              <thead className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <tr>
                  <th className="py-3 px-6 text-left">Estado</th>
                  <th className="py-3 px-6 text-right">Nº orçamentos</th>
                  <th className="py-3 px-6 text-right">Valor total</th>
                </tr>
              </thead>
              <tbody>
                {orcamentos.por_estado.map((row) => (
                  <tr key={row.chave} className="border-b border-zinc-100">
                    <td className="py-3 px-6 text-neutral-950 font-semibold">{row.chave}</td>
                    <td className="py-3 px-6 text-right text-neutral-700 font-mono">{row.count}</td>
                    <td className="py-3 px-6 text-right text-neutral-950 font-semibold">{eur(row.valor_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Registos por utilizador */}
      <section data-testid="section-registos-utilizador">
        <SectionHeader
          eyebrow="Produtividade"
          title="Registos por utilizador"
          icon={BarChart3}
          hint="Número de serviços/registos criados por cada utilizador (produtividade individual)."
        />
        {servicos_por_utilizador.length === 0 ? (
          <EmptyBox text="Sem registos no intervalo selecionado." />
        ) : (
          <UserTable
            testid="tbl-registos-utilizador"
            rows={servicos_por_utilizador}
            columns={[
              { key: "email", label: "Utilizador", type: "primary" },
              { key: "name", label: "Nome" },
              { key: "count", label: "Nº registos", align: "right", mono: true, format: (v) => v },
              { key: "valor_total", label: "Valor total", align: "right", format: eur, strong: true },
            ]}
          />
        )}
      </section>

      {/* Acessos por utilizador */}
      <section data-testid="section-acessos-utilizador">
        <SectionHeader
          eyebrow="Segurança"
          title="Acessos por utilizador"
          icon={LogIn}
          hint="Contagem de logins bem sucedidos por utilizador e último acesso registado."
        />
        {acessos_por_utilizador.length === 0 ? (
          <EmptyBox text="Sem acessos registados no intervalo selecionado." />
        ) : (
          <UserTable
            testid="tbl-acessos-utilizador"
            rows={acessos_por_utilizador}
            columns={[
              { key: "email", label: "Utilizador", type: "primary" },
              { key: "name", label: "Nome" },
              { key: "count", label: "Nº acessos", align: "right", mono: true, format: (v) => v },
              { key: "last_at", label: "Último acesso", format: formatDateTime },
            ]}
          />
        )}
      </section>

      {/* Totals summary */}
      <section data-testid="section-totais">
        <SectionHeader eyebrow="Visão geral" title="Totais da plataforma" icon={Users} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SmallStat label="Condomínios" value={totais.condominios} />
          <SmallStat label="Empresas" value={totais.empresas} />
          <SmallStat label="Serviços" value={totais.servicos} />
          <SmallStat label="Consultas" value={totais.consultas} />
          <SmallStat label="Utilizadores" value={totais.utilizadores} />
          <SmallStat label="Serviços pendentes" value={totais.servicos_pendentes} />
          <SmallStat label="Consultas pendentes" value={totais.consultas_pendentes} />
          <SmallStat label="Faturação total" value={eur(totais.valor_servicos_total)} icon={Euro} />
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ eyebrow, title, icon: Icon, hint }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-9 h-9 bg-[#0047FF]/10 border border-[#0047FF]/20 rounded-sm flex items-center justify-center shrink-0 mt-0.5">
            <Icon className="w-4 h-4 text-[#0047FF]" strokeWidth={2} />
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
            {eyebrow}
          </div>
          <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight leading-tight">
            {title}
          </h2>
          {hint && <p className="text-xs font-manrope text-neutral-500 mt-1 max-w-xl">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

function BigStat({ label, value, testid, accent = "#0047FF" }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
          {label}
        </div>
        <ArrowUpRight className="w-3.5 h-3.5" style={{ color: accent }} strokeWidth={2} />
      </div>
      <div className="text-3xl font-chivo font-black text-neutral-950 leading-none">{value}</div>
    </div>
  );
}

function SmallStat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-neutral-400" strokeWidth={2} />}
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
          {label}
        </div>
      </div>
      <div className="text-2xl font-chivo font-black text-neutral-950 leading-none">{value}</div>
    </div>
  );
}

function EmptyBox({ text }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center text-sm font-manrope text-neutral-500">
      {text}
    </div>
  );
}

function UserTable({ rows, columns, testid }) {
  const maxCount = Math.max(1, ...rows.map((r) => Number(r.count) || 0));
  return (
    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm font-manrope">
        <thead className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`py-3 px-6 ${c.align === "right" ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const share = ((Number(row.count) || 0) / maxCount) * 100;
            return (
              <tr key={`${row.email || row.chave || i}`} className="border-b border-zinc-100 relative">
                {columns.map((c) => {
                  const raw = row[c.key];
                  const val = c.format ? c.format(raw) : raw ?? "—";
                  const cls = `py-3 px-6 ${c.align === "right" ? "text-right" : "text-left"} ${
                    c.mono ? "font-mono" : ""
                  } ${c.strong ? "text-neutral-950 font-semibold" : c.type === "primary" ? "text-neutral-950 font-semibold" : "text-neutral-700"}`;
                  if (c.type === "primary") {
                    return (
                      <td key={c.key} className={cls}>
                        <div className="relative">
                          <div
                            className="absolute inset-y-0 left-0 bg-[#0047FF]/5"
                            style={{ width: `${share}%` }}
                          />
                          <span className="relative">{val || "—"}</span>
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className={cls}>
                      {val || "—"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
